import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { captureException } from '@sentry/react';

import { HttpError } from '../../api';
import { isSessionLoss } from '../../api/session';
import { HOME_PATH } from '../../access';
import { announceSessionSwitch, endViewAs, startViewAs, useSignedInUser, useViewingAs } from '../../session';
import type { ViewablePerson } from '../../session';
import { VIEWAS_STOP_VIEWING, VIEWAS_SWITCH_USER } from '../../modules/analytics/events';
import { trackEvent } from '../../modules/analytics/trackingMiddleware';
import { forgetSignedInPerson } from '../../utilities/signedInPerson';

const VIEW_AS_MUTATION_KEY = ['session', 'viewAs'] as const;

export type StartRefusal = 'notAllowed' | 'targetUnavailable' | 'alreadyViewing' | 'self' | 'failed';

const REFUSAL_OF_CODE: Readonly<Record<string, StartRefusal | undefined>> = {
  NOT_ALLOWED: 'notAllowed',
  TARGET_UNAVAILABLE: 'targetUnavailable',
  ALREADY_VIEWING: 'alreadyViewing',
  SELF: 'self',
};

const endedTheSession = (error: unknown): boolean => error instanceof HttpError && isSessionLoss(error.status, { code: error.code });

const refusalOf = (error: unknown): StartRefusal | null => {
  if (error === null || endedTheSession(error)) return null;
  const code = error instanceof HttpError ? error.code : null;
  return (code === null ? undefined : REFUSAL_OF_CODE[code]) ?? 'failed';
};

const openAsSwitchedSession = (userId: string, landingPage: string | null): void => {
  forgetSignedInPerson();
  announceSessionSwitch(userId);
  window.location.assign(landingPage ?? HOME_PATH);
};

export interface StartViewAs {
  readonly start: (person: ViewablePerson) => void;
  readonly starting: boolean;
  readonly refusal: StartRefusal | null;
}

export const useStartViewAs = (): StartViewAs => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: VIEW_AS_MUTATION_KEY,
    mutationFn: (person: ViewablePerson) => startViewAs(person.id),
    onSuccess: (answer, person) => {
      trackEvent(VIEWAS_SWITCH_USER, { targetUserId: person.id, targetUserName: person.name });
      openAsSwitchedSession(person.id, answer.landingPage);
    },
    onError: (error: unknown) => {
      if (refusalOf(error) === 'failed') captureException(error, { tags: { feature: 'view-as' } });
    },
  });

  return {
    start: (person) => {
      if (queryClient.isMutating({ mutationKey: VIEW_AS_MUTATION_KEY }) > 0) return;
      mutation.mutate(person);
    },
    starting: mutation.isPending || mutation.isSuccess,
    refusal: refusalOf(mutation.error),
  };
};

export const useStopViewAs = (): (() => void) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { id: viewedUserId } = useSignedInUser();
  const viewingAs = useViewingAs();
  const { mutate } = useMutation({
    mutationKey: VIEW_AS_MUTATION_KEY,
    mutationFn: endViewAs,
    onSuccess: (answer) => {
      trackEvent(VIEWAS_STOP_VIEWING, { targetUserId: viewedUserId });
      openAsSwitchedSession(viewingAs?.id ?? viewedUserId, answer.landingPage);
    },
    onError: (error: unknown) => {
      if (endedTheSession(error)) return;
      captureException(error, { tags: { feature: 'view-as' } });
      toast.error(t('viewAs.toast.stopFailed', 'Couldn’t stop View As. Check your connection and try again.'));
    },
  });

  return () => {
    if (queryClient.isMutating({ mutationKey: VIEW_AS_MUTATION_KEY }) > 0) return;
    mutate();
  };
};
